import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useEffect, useState, useRef } from "react";
import DialogContainer from "../../../components/dialog-container/dialog-container";
import { useFormik } from "formik";
import * as Yup from "yup";
import ErrorNotification from "../../../components/error-notification/error-notification";
import Loading from "../../../components/loading/loading";
import Notification from "../../../components/notification/notification";
import InputNumber from "../../../components/input-number/input-number";
import Contacts from "./contacts/contacts";
import Attachments from "./attachments/attachments";
import OptionalOrderDetails from "./optional-order-details/optional-order-details";
import AdditionalDetails from "./additional-details/additional-details";
import ProjectSection from "./project-section/project-section";
import PropertySection from "./property-section/property-section";
import { useDispatch, useSelector } from "react-redux";
import {
  getClientAllUsersRequest,
  getClientsListRequest,
  getClientSubDivisionListRequest,
  getFormDetailsRequest,
  getFormListByClientIdRequest,
  getProcessesProcedureRequest,
  resetClientData,
} from "../../../redux/clients/slice";
import {
  getAdjustmentTypesRequest,
  getProjectsRequest,
  resetOrdersData,
  getOrderDetailsRequest,
  getOrderDetailsSuccess,
} from "../../../redux/orders/slice";
import { addOrderRequest } from "../../../utils/APICalls";
import Fees from "./fees/fees";
import { resetPropertyData } from "../../../redux/properties/slice";
import "./add-order.css";
import eventBus from "../../../utils/EventBus";

type props = {
  showDialog: boolean;
  closeDialog: any;
  addOrder: Function;
  orderId?: string;
};

interface ExternalContact {
  fullName: string;
  email: string;
}

type FormValues = {
  clientId: string;
  subDivision: string;
  project: string;
  requesterId: string;
  isContactInspection: boolean;
  inspectionScheduleType: string;
  photoCount: number;
  option1: string | null;
  option2: string | null;
  option3: string | null;
  propertyId: string;
  formId: string;
  startDateToday: boolean;
  startDate: string;
  appointmentDate: string | null;
  inspectorDueDate: string | null;
  clientDueDate: string;
  isRushInspection: boolean;
  rushFeeId: string;
  contacts: {
    showVisionUsers: boolean;
    showExtraContacts: boolean;
    includeRequester: boolean;
    contactIds: string[];
    externalContacts: ExternalContact[];
  };
  fees: any[];
  notes: any[];
  attachments: any[];
};

export default function AddOrderDialog({
  showDialog,
  closeDialog,
  addOrder,
  orderId,
}: props) {
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isPopulatingFromOrder, setIsPopulatingFromOrder] = useState(false);
  const [selectedFormDetails, setSelectedFormDetails] = useState<any>(null);
  const [isUploadingAttachment, SetIsUploadingAttachment] = useState(false);

  const clientSelectRef = useRef<HTMLDivElement>(null);

  const {
    loading,
    clientsList,
    subDivisionList,
    clientAllUsers,
    formsList,
    processesProcedure,
    formDetails,
  } = useSelector((state: any) => state.clients);

  const {
    projects,
    loading: projectsLoading,
    adjustmentTypes,
    orderDetails,
  } = useSelector((state: any) => state.orders);

  const dispatch = useDispatch();

  const isReorderInitialLoading =
    !!orderId && showDialog && (!orderDetails || isPopulatingFromOrder);

  const requiredFieldMessage = "This field can't be empty";
  const dateOnOrAfterStartDate = (fieldLabel: string) =>
    Yup.string()
      .nullable()
      .optional()
      .test(
        "date-validation",
        `${fieldLabel} cannot be before start date`,
        function (value) {
          const { startDate } = this.parent;
          if (!value || !startDate) return true;
          return new Date(value) >= new Date(startDate);
        }
      );

  const externalContactSchema = Yup.object({
    fullName: Yup.string().required(requiredFieldMessage),
    email: Yup.string()
      .email("Invalid email format")
      .required(requiredFieldMessage)
      .test("unique-email", "Email address must be unique", function (value) {
        if (!value) return true;
        const { path } = this;
        const contacts = this.from?.[1]?.value?.externalContacts || [];
        const emails = contacts
          .map((c: any) => c.email)
          .filter((email: string) => email);
        const currentIndex = parseInt(
          path.split("[")[1]?.split("]")[0] || "0",
          10
        );
        const duplicateCount = emails.filter(
          (email: string, index: number) =>
            email.toLowerCase() === value.toLowerCase() &&
            index !== currentIndex
        ).length;
        return duplicateCount === 0;
      }),
  });

  const validationSchema = Yup.object({
    clientId: Yup.string().required(requiredFieldMessage),
    requesterId: Yup.string().required(requiredFieldMessage),
    propertyId: Yup.string().required(requiredFieldMessage),
    formId: Yup.string().required(requiredFieldMessage),
    startDate: Yup.string().required(requiredFieldMessage),
    appointmentDate: dateOnOrAfterStartDate("Appointment date"),
    inspectorDueDate: dateOnOrAfterStartDate("Inspector due date")
      .nullable()
      .required(requiredFieldMessage),
    clientDueDate: dateOnOrAfterStartDate("Client due date")
      .nullable()
      .required(requiredFieldMessage),
    rushFeeId: Yup.string().when("isRushInspection", {
      is: true,
      then: (schema) => schema.required(requiredFieldMessage),
      otherwise: (schema) => schema.optional(),
    }),
    photoCount: Yup.number()
      .typeError("Photo count must be a number")
      .required(requiredFieldMessage),
    option1: Yup.string().nullable(),
    option2: Yup.string().nullable(),
    option3: Yup.string().nullable(),
    contacts: Yup.object({
      showVisionUsers: Yup.boolean(),
      showExtraContacts: Yup.boolean(),
      contactIds: Yup.array().when("showVisionUsers", {
        is: true,
        then: (schema) => schema.min(1, requiredFieldMessage),
        otherwise: (schema) => schema.optional(),
      }),
      externalContacts: Yup.array().when("showExtraContacts", {
        is: true,
        then: (schema) =>
          schema.of(externalContactSchema).min(1, requiredFieldMessage),
        otherwise: (schema) => schema.optional(),
      }),
    }),
    fees: Yup.array(),
    notes: Yup.array(),
    attachments: Yup.array(),
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      clientId: "",
      subDivision: "",
      project: "",
      requesterId: "",
      isContactInspection: true,
      inspectionScheduleType: "Appointment",
      photoCount: 0,
      option1: null,
      option2: null,
      option3: null,
      propertyId: "",
      formId: "",
      startDateToday: false,
      startDate: "",
      appointmentDate: "",
      inspectorDueDate: "",
      clientDueDate: "",
      isRushInspection: false,
      rushFeeId: "",
      contacts: {
        showVisionUsers: false,
        showExtraContacts: false,
        includeRequester: false,
        contactIds: [],
        externalContacts: [{ fullName: "", email: "" }],
      },
      fees: [],
      notes: [],
      attachments: [],
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setShowLoading(true);
        const payload: any = {
          clientId: values.clientId,
          ...(values.subDivision && {
            subDivisionId: values.subDivision,
          }),
          requesterId: values.requesterId,
          ...(values.project && {
            projectId: values.project,
          }),
          isContactInspection: values.isContactInspection,
          inspectionScheduleType: values.inspectionScheduleType,
          photoCount: values.photoCount,
          propertyId: values.propertyId,
          formId: values.formId,
          startDate: values.startDate,
          ...(values.appointmentDate && {
            appointmentDate: values.appointmentDate,
          }),
          ...(values.inspectorDueDate && {
            inspectorDueDate: values.inspectorDueDate,
          }),
          ...(values.clientDueDate && {
            clientDueDate: values.clientDueDate,
          }),
          isRushInspection: values.isRushInspection,
          ...(values.rushFeeId && {
            rushFeeId: values.rushFeeId,
          }),
          contacts: {
            includeRequester: values.contacts.includeRequester,
            contactIds: values.contacts.contactIds,
            externalContacts: values.contacts.externalContacts.filter(
              (contact) =>
                contact.fullName.trim() !== "" && contact.email.trim() !== ""
            ),
          },
          fees: values.fees || [],
          notes: values.notes || [],
          attachments: values.attachments || [],
        };
        if (values.option1) {
          payload.option1 = values.option1;
        }
        if (values.option2) {
          payload.option2 = values.option2;
        }
        if (values.option3) {
          payload.option3 = values.option3;
        }

        const response = await addOrderRequest(payload);

        if (
          (response && response.status === 200) ||
          (response && response.status === 201)
        ) {
          addOrder();
          hideDialog();
        } else {
          const errorMsg =
            response?.data?.message ||
            "Failed to create order. Please try again.";
          setErrorMessage(errorMsg);
          setShowErrorNotification(true);
        }
      } catch (error) {
        setErrorMessage("An unexpected error occurred. Please try again.");
        setShowErrorNotification(true);
      } finally {
        setShowLoading(false);
      }
    },
  });

  useEffect(() => {
    dispatch(getClientsListRequest());
    dispatch(getAdjustmentTypesRequest());
    eventBus.addListener("uploadingAttachment", onUploadingAttachment);
    return () => {
      eventBus.removeListener("uploadingAttachment", onUploadingAttachment);
    };
  }, []); // eslint-disable-line

  const onUploadingAttachment = (isUploading: boolean) => {
    SetIsUploadingAttachment(isUploading);
  };

  useEffect(() => {
    if (showDialog && orderId) {
      dispatch(getOrderDetailsSuccess({ response: null }));
      dispatch(getOrderDetailsRequest({ id: orderId }));
      formik.setFieldValue("startDateToday", false);
      formik.setFieldValue("startDate", "");
      formik.setFieldValue("appointmentDate", "");
      formik.setFieldValue("inspectorDueDate", "");
      formik.setFieldValue("clientDueDate", "");
    } else if (!showDialog) {
      dispatch(getOrderDetailsSuccess({ response: null }));
    }
  }, [showDialog, orderId]); // eslint-disable-line

  useEffect(() => {
    if (showDialog && clientSelectRef.current) {
      setTimeout(() => {
        const selectElement = clientSelectRef.current?.querySelector(
          '[role="combobox"]'
        ) as HTMLElement;
        if (selectElement) {
          selectElement.focus();
        }
      }, 100);
    }
  }, [showDialog]);

  useEffect(() => {
    if (!formik.values.clientId && showDialog) {
      const handleFocusOut = (event: FocusEvent) => {
        const target = event.target as HTMLElement;
        if (
          target &&
          target.closest("form") &&
          !target.closest("[data-client-select]")
        ) {
          setTimeout(() => {
            const selectElement = clientSelectRef.current?.querySelector(
              '[role="combobox"]'
            ) as HTMLElement;
            if (selectElement) {
              selectElement.focus();
            }
          }, 0);
        }
      };

      document.addEventListener("focusout", handleFocusOut);
      return () => document.removeEventListener("focusout", handleFocusOut);
    }
  }, [formik.values.clientId, showDialog]);

  useEffect(() => {
    const selectedClient = formik.values.clientId;
    if (selectedClient && selectedClient.toString().trim() !== "") {
      dispatch(getClientSubDivisionListRequest({ id: selectedClient }));
      dispatch(getProjectsRequest({ clientId: selectedClient }));
      dispatch(getClientAllUsersRequest({ id: selectedClient }));
      dispatch(getFormListByClientIdRequest({ clientId: selectedClient }));
      dispatch(getProcessesProcedureRequest({ id: selectedClient }));
    }
  }, [formik.values.clientId]); // eslint-disable-line

  useEffect(() => {
    const selectedForm = formik.values.formId;
    setSelectedFormDetails(null);
    if (selectedForm && selectedForm.toString().trim() !== "") {
      dispatch(getFormDetailsRequest({ id: selectedForm }));
    }
  }, [formik.values.formId]); // eslint-disable-line

  useEffect(() => {
    const selectedForm = formik.values.formId;
    if (!selectedForm || !formDetails) return;
    const formDetailsId = formDetails?.id || formDetails?.formId;
    if (formDetailsId && String(formDetailsId) === String(selectedForm)) {
      setSelectedFormDetails(formDetails);
    }
  }, [formDetails, formik.values.formId]);

  const hideDialog = () => {
    formik.resetForm();
    dispatch(resetClientData());
    dispatch(resetOrdersData());
    dispatch(resetPropertyData());
    dispatch(getOrderDetailsSuccess({ response: null }));
    setIsPopulatingFromOrder(false);
    closeDialog();
  };

  useEffect(() => {
    if (!formik.values.isContactInspection) {
      formik.setFieldValue("inspectionScheduleType", "Unannounced");
    }
  }, [formik.values.isContactInspection]); // eslint-disable-line

  useEffect(() => {
    if (formik.values.startDateToday) {
      const today = new Date();
      const formattedDate = today.toISOString().split("T")[0];
      formik.setFieldValue("startDate", formattedDate);
    }
  }, [formik.values.startDateToday]); // eslint-disable-line

  useEffect(() => {
    if (formik.values.startDate) {
      formik.validateField("appointmentDate");
      formik.validateField("inspectorDueDate");
      formik.validateField("clientDueDate");
    }
  }, [formik.values.startDate]); // eslint-disable-line

  useEffect(() => {
    if (formik.values.clientId && formik.values.clientId !== "") {
      formik.setFieldValue("subDivision", "");
      formik.setFieldValue("project", "");
      formik.setFieldValue("requesterId", "");
      formik.setFieldValue("propertyId", "");
      formik.setFieldValue("formId", "");
      formik.setFieldValue("contacts", {
        showVisionUsers: false,
        showExtraContacts: false,
        includeRequester: false,
        contactIds: [],
        externalContacts: [{ fullName: "", email: "" }],
      });
      formik.setFieldValue("fees", []);
      formik.setFieldValue("notes", []);
      formik.setFieldValue("attachments", []);
      formik.setFieldValue("rushFeeId", "");
      dispatch(resetPropertyData());
    }
  }, [formik.values.clientId]); // eslint-disable-line

  useEffect(() => {
    if (!orderDetails || !orderId || !showDialog || isPopulatingFromOrder)
      return;
    if (orderDetails?.id !== orderId) return;

    const propertyId =
      orderDetails?.propertyRecord?.id || orderDetails?.propertyId || "";

    setIsPopulatingFromOrder(true);

    if (orderDetails?.client?.id) {
      formik.setFieldValue("clientId", orderDetails.client.id);

      const populateFormValues = () => {
        let rushFeeIdValue = "";
        if (orderDetails?.rushFeeLabel && processesProcedure?.rushFees) {
          const matchedRushFee = processesProcedure.rushFees.find(
            (rush: any) => rush.label === orderDetails.rushFeeLabel
          );
          rushFeeIdValue = matchedRushFee?.id || "";
        }

        formik.setValues({
          clientId: orderDetails?.client?.id || "",
          subDivision: orderDetails?.subDivision?.id || "",
          project: orderDetails?.project?.id || "",
          requesterId: orderDetails?.requester?.id || "",
          isContactInspection: orderDetails?.isContactInspection ?? true,
          inspectionScheduleType:
            orderDetails?.inspectionScheduleType || "Appointment",
          photoCount: orderDetails?.photoCount || 0,
          option1: orderDetails?.option1 || null,
          option2: orderDetails?.option2 || null,
          option3: orderDetails?.option3 || null,
          propertyId: propertyId || "",
          formId: orderDetails?.form?.id || "",
          startDateToday: false,
          startDate: "",
          appointmentDate: "",
          inspectorDueDate: "",
          clientDueDate: "",
          isRushInspection: !!orderDetails?.rushFeeLabel || false,
          rushFeeId: rushFeeIdValue,
          contacts: {
            showVisionUsers:
              orderDetails?.contact?.internalContacts
                ?.filter((c: any) => !c.isRequestor)
                .map((c: any) => c.userId)?.length > 0,
            showExtraContacts:
              orderDetails?.contact?.externalContacts?.length > 0,
            includeRequester:
              orderDetails?.contact?.internalContacts?.some(
                (c: any) => c.isRequestor
              ) || false,
            contactIds:
              orderDetails?.contact?.internalContacts
                ?.filter((c: any) => !c.isRequestor)
                .map((c: any) => c.userId) || [],
            externalContacts: (orderDetails?.contact?.externalContacts?.length >
              0 &&
              orderDetails?.contact?.externalContacts?.map((c: any) => ({
                fullName: c.fullName || "",
                email: c.email || "",
              }))) || [{ fullName: "", email: "" }],
          },
          fees: orderDetails?.fees || [],
          notes: orderDetails?.notes || [],
          attachments: orderDetails?.attachments || [],
        });

        setIsPopulatingFromOrder(false);

        // Mark all fields as touched and validate so validation errors are visible
        setTimeout(() => {
          const externalContactsTouched =
            formik.values.contacts.externalContacts?.map(() => ({
              fullName: true,
              email: true,
            })) || [];

          formik.setTouched({
            clientId: true,
            subDivision: true,
            project: true,
            requesterId: true,
            isContactInspection: true,
            inspectionScheduleType: true,
            photoCount: true,
            option1: true,
            option2: true,
            option3: true,
            propertyId: true,
            formId: true,
            startDate: true,
            appointmentDate: true,
            inspectorDueDate: true,
            clientDueDate: true,
            isRushInspection: true,
            rushFeeId: true,
            contacts: {
              showVisionUsers: true,
              showExtraContacts: true,
              includeRequester: true,
              contactIds: true,
              externalContacts: externalContactsTouched,
            },
          });
          formik.validateForm();
        }, 500);

        // Trigger form details load after formId is set in formik
        if (orderDetails?.form?.id) {
          // Use setTimeout to ensure formik has updated
          setTimeout(() => {
            dispatch(getFormDetailsRequest({ id: orderDetails.form.id }));
          }, 300);
        }
      };

      // Wait for formsList and processesProcedure to be ready (for form dropdown and rush fees)
      let checkCount = 0;
      const maxChecks = 25; // Maximum 5 seconds (25 * 200ms)
      const checkAndPopulate = () => {
        checkCount++;

        const formsLoaded = formsList && formsList.length > 0;
        const processesLoaded =
          !orderDetails?.rushFeeLabel ||
          (processesProcedure && processesProcedure.rushFees);

        if (formsLoaded && processesLoaded) {
          populateFormValues();
        } else if (checkCount < maxChecks) {
          setTimeout(() => {
            checkAndPopulate();
          }, 200);
        } else {
          // Timeout - populate anyway with what we have
          populateFormValues();
        }
      };

      // Start checking after initial delay
      setTimeout(() => {
        checkAndPopulate();
      }, 800);
    } else {
      setIsPopulatingFromOrder(false);
    }
  }, [orderDetails, orderId, showDialog, processesProcedure]); // eslint-disable-line

  return (
    <DialogContainer
      title={orderId ? "Re-Order Inspection" : "New Order"}
      showDialog={showDialog}
      closeDialog={hideDialog}
      buttonText="Create Order"
      onSubmit={formik.handleSubmit}
      saveButtonDisabled={isUploadingAttachment}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <form onSubmit={formik.handleSubmit}>
          <Box
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: "#FAFAFA",
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
            }}
          >
            <Typography variant="h5" sx={{ mb: 2 }}>
              Order Details
            </Typography>
            {/* Two-column layout: Row 1 - Client | Sub Division */}
            <Grid container spacing={2} sx={{ mb: 2, width: "100%" }}>
              <Grid
                size={{
                  xs: 12,
                  sm: subDivisionList && subDivisionList.length > 0 ? 6 : 12,
                }}
              >
                <FormControl
                  ref={clientSelectRef}
                  data-client-select
                  fullWidth
                  size="small"
                  error={
                    formik.touched.clientId && Boolean(formik.errors.clientId)
                  }
                >
                  <InputLabel id="client-label">Client *</InputLabel>
                  <Select
                    labelId="client-label"
                    id="clientId"
                    name="clientId"
                    value={formik.values.clientId}
                    onChange={formik.handleChange}
                    label="Client *"
                    autoFocus={!orderId}
                    disabled={!!orderId}
                  >
                    {clientsList?.map((client: any) => (
                      <MenuItem key={client.id} value={client.id}>
                        {client.clientName}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.clientId && formik.errors.clientId && (
                    <Typography
                      color="error"
                      variant="caption"
                      sx={{ mt: 0.5 }}
                    >
                      {formik.errors.clientId}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              {subDivisionList && subDivisionList?.length > 0 && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="sub-division-label">
                      Sub Division
                    </InputLabel>
                    <Select
                      labelId="sub-division-label"
                      id="subDivision"
                      name="subDivision"
                      value={formik.values.subDivision}
                      onChange={formik.handleChange}
                      label="Sub Division"
                      disabled={!formik.values.clientId}
                    >
                      {subDivisionList?.map((subDivision: any) => (
                        <MenuItem key={subDivision.id} value={subDivision.id}>
                          {subDivision.displayName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>

            {/* Row 2 - Assign to Project? | Requestor */}
            <Grid container spacing={2} sx={{ mb: 2, width: "100%" }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <ProjectSection
                  formik={formik}
                  projects={projects}
                  setShowErrorNotification={setShowErrorNotification}
                  setErrorMessage={setErrorMessage}
                  setShowLoading={setShowLoading}
                  setShowSuccessNotification={setShowSuccessNotification}
                  setSuccessMessage={setSuccessMessage}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl
                  fullWidth
                  size="small"
                  error={
                    formik.touched.requesterId &&
                    Boolean(formik.errors.requesterId)
                  }
                >
                  <InputLabel id="requestor-label">Requestor *</InputLabel>
                  <Select
                    labelId="requestor-label"
                    id="requesterId"
                    name="requesterId"
                    value={formik.values.requesterId}
                    onChange={formik.handleChange}
                    label="Requestor *"
                    disabled={!formik.values.clientId}
                  >
                    {clientAllUsers?.map((user: any) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.fullName}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.requesterId && formik.errors.requesterId && (
                    <Typography
                      color="error"
                      variant="caption"
                      sx={{ mt: 0.5 }}
                    >
                      {formik.errors.requesterId}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            </Grid>

            {/* Row 3 - Inspection Type | Appointment or Unannounced? */}
            <Grid container spacing={2} sx={{ mb: 2, width: "100%" }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="inspection-type-label">
                    Inspection Type *
                  </InputLabel>
                  <Select
                    labelId="inspection-type-label"
                    id="isContactInspection"
                    name="isContactInspection"
                    value={String(formik.values.isContactInspection)}
                    onChange={(e) => {
                      formik.setFieldValue(
                        "isContactInspection",
                        e.target.value === "true"
                      );
                    }}
                    label="Inspection Type *"
                    disabled={!formik.values.clientId}
                  >
                    <MenuItem value="true">Contact</MenuItem>
                    <MenuItem value="false">No Contact</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl
                  fullWidth
                  size="small"
                  error={
                    formik.touched.inspectionScheduleType &&
                    Boolean(formik.errors.inspectionScheduleType)
                  }
                >
                  <InputLabel id="schedule-type-label">
                    Appointment or Unannounced?
                  </InputLabel>
                  <Select
                    labelId="schedule-type-label"
                    id="inspectionScheduleType"
                    name="inspectionScheduleType"
                    value={formik.values.inspectionScheduleType}
                    onChange={formik.handleChange}
                    label="Appointment or Unannounced?"
                    disabled={!formik.values.clientId}
                  >
                    <MenuItem value="Appointment">Appointment</MenuItem>
                    <MenuItem value="Unannounced">Unannounced</MenuItem>
                  </Select>
                  {formik.touched.inspectionScheduleType &&
                    formik.errors.inspectionScheduleType && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 0.5 }}
                      >
                        {formik.errors.inspectionScheduleType}
                      </Typography>
                    )}
                </FormControl>
              </Grid>
            </Grid>

            {/* Row 4 - Photo Count (single column, same width as grid column) */}
            <Grid container spacing={2} sx={{ mb: 2, width: "100%" }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InputNumber
                  id="photoCount"
                  name="photoCount"
                  label="Photo Count *"
                  size="small"
                  fullWidth
                  variant="outlined"
                  placeholder="Enter a number"
                  value={formik.values.photoCount}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.photoCount &&
                    Boolean(formik.errors.photoCount)
                  }
                  helperText={
                    formik.touched.photoCount && formik.errors.photoCount
                  }
                  allowDecimal={false}
                  disabled={!formik.values.clientId}
                />
              </Grid>
            </Grid>

            {/* Who should we contact about this order? */}
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 500, mb: 1.5, color: "text.primary" }}
              >
                Who should we contact about this order?
              </Typography>
              <Contacts formik={formik} clientAllUsers={clientAllUsers} />
            </Box>
          </Box>

          <Box
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: "#FAFAFA",
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
            }}
          >
            <PropertySection
              propertyId={formik.values.propertyId}
              onPropertyChange={(value) =>
                formik.setFieldValue("propertyId", value)
              }
              error={formik.errors.propertyId}
              touched={formik.touched.propertyId}
              clientId={formik.values.clientId}
              onPropertySuccess={(message) => {
                setSuccessMessage(message);
                setShowSuccessNotification(true);
              }}
            />
          </Box>
          {/* Form card */}
          <Box
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: "#FAFAFA",
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="h5"
                sx={{ fontWeight: 400, mb: 2, fontSize: "24px" }}
              >
                Form
              </Typography>
            </Box>
            <Grid container spacing={2} sx={{ width: "100%" }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl
                  fullWidth
                  size="small"
                  error={formik.touched.formId && Boolean(formik.errors.formId)}
                >
                  <InputLabel id="form-label">Form to Use</InputLabel>
                  <Select
                    labelId="form-label"
                    id="formId"
                    name="formId"
                    value={formik.values.formId}
                    onChange={formik.handleChange}
                    label="Form to Use"
                    disabled={!formik.values.clientId}
                  >
                    {formsList?.map((form: any) => (
                      <MenuItem key={form?.id} value={form?.id}>
                        {form?.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.formId && formik.errors.formId && (
                    <Typography
                      color="error"
                      variant="caption"
                      sx={{ mt: 0.5 }}
                    >
                      {formik.errors.formId}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {/* Dates card */}
          <Box
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: "#FAFAFA",
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
            }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: 400, mb: 2, fontSize: "24px" }}
            >
              Dates
            </Typography>
            <Grid container spacing={2} sx={{ width: "100%" }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <DatePicker
                  label="Start Date"
                  value={
                    formik.values.startDate
                      ? dayjs(formik.values.startDate)
                      : null
                  }
                  onChange={(date) => {
                    formik.setFieldValue(
                      "startDate",
                      date?.format("YYYY-MM-DD")
                    );
                  }}
                  format="MMMM D, YYYY"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      variant: "outlined",
                      placeholder: "Select Date",
                      error:
                        formik.touched.startDate &&
                        Boolean(formik.errors.startDate),
                      helperText:
                        formik.touched.startDate && formik.errors.startDate,
                    },
                  }}
                  disabled={!formik.values.clientId}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <DatePicker
                  label="Appointment Date/Estimated Completion Date"
                  value={
                    formik.values.appointmentDate
                      ? dayjs(formik.values.appointmentDate)
                      : null
                  }
                  onChange={(date) => {
                    formik.setFieldValue(
                      "appointmentDate",
                      date?.format("YYYY-MM-DD")
                    );
                    formik.setFieldTouched("appointmentDate", true);
                    formik.validateField("appointmentDate");
                  }}
                  format="MMMM D, YYYY"
                  minDate={
                    formik.values.startDate
                      ? dayjs(formik.values.startDate)
                      : dayjs()
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      variant: "outlined",
                      placeholder: "Select Date",
                      error:
                        formik.touched.appointmentDate &&
                        Boolean(formik.errors.appointmentDate),
                      helperText:
                        formik.touched.appointmentDate &&
                        formik.errors.appointmentDate,
                    },
                  }}
                  disabled={!formik.values.clientId}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <DatePicker
                  label="Inspector Due Date"
                  value={
                    formik.values.inspectorDueDate
                      ? dayjs(formik.values.inspectorDueDate)
                      : null
                  }
                  onChange={(date) => {
                    formik.setFieldValue(
                      "inspectorDueDate",
                      date?.format("YYYY-MM-DD")
                    );
                    formik.setFieldTouched("inspectorDueDate", true);
                    setTimeout(() => {
                      formik.validateField("inspectorDueDate");
                    }, 0);
                  }}
                  format="MMMM D, YYYY"
                  minDate={
                    formik.values.startDate
                      ? dayjs(formik.values.startDate)
                      : dayjs()
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      variant: "outlined",
                      placeholder: "Select Date",
                      error:
                        formik.touched.inspectorDueDate &&
                        Boolean(formik.errors.inspectorDueDate),
                      helperText:
                        formik.touched.inspectorDueDate &&
                        formik.errors.inspectorDueDate,
                    },
                  }}
                  disabled={!formik.values.clientId}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <DatePicker
                  label="Client Due Date"
                  value={
                    formik.values.clientDueDate
                      ? dayjs(formik.values.clientDueDate)
                      : null
                  }
                  onChange={(date) => {
                    const dateValue = date?.format("YYYY-MM-DD");
                    formik.setFieldValue("clientDueDate", dateValue);
                    formik.setFieldTouched("clientDueDate", true);
                    setTimeout(() => {
                      formik.validateField("clientDueDate");
                    }, 0);
                  }}
                  format="MMMM D, YYYY"
                  minDate={
                    formik.values.startDate
                      ? dayjs(formik.values.startDate)
                      : dayjs()
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      variant: "outlined",
                      placeholder: "Select Date",
                      error:
                        formik.touched.clientDueDate &&
                        Boolean(formik.errors.clientDueDate),
                      helperText:
                        formik.touched.clientDueDate &&
                        formik.errors.clientDueDate,
                    },
                  }}
                  disabled={!formik.values.clientId}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl
                  fullWidth
                  size="small"
                  error={
                    formik.touched.rushFeeId && Boolean(formik.errors.rushFeeId)
                  }
                >
                  <InputLabel id="rush-type-label">Rush Inspection?</InputLabel>
                  <Select
                    labelId="rush-type-label"
                    id="rushFeeId"
                    name="rushFeeId"
                    value={formik.values.rushFeeId || ""}
                    onChange={(e) => {
                      const val = e.target.value as string;
                      formik.setFieldValue("rushFeeId", val);
                      formik.setFieldValue("isRushInspection", !!val);
                      formik.setFieldTouched("rushFeeId", true);
                    }}
                    label="Rush Inspection?"
                    disabled={!formik.values.clientId}
                  >
                    <MenuItem value="">No Rush</MenuItem>
                    {processesProcedure?.rushFees?.map((rush: any) => (
                      <MenuItem key={rush?.id} value={rush?.id}>
                        {rush?.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.rushFeeId && formik.errors.rushFeeId && (
                    <Typography
                      color="error"
                      variant="caption"
                      sx={{ mt: 0.5 }}
                    >
                      {formik.errors.rushFeeId}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </Box>
          <Fees
            data={processesProcedure?.rushFees || []}
            formik={formik}
            formDetails={selectedFormDetails}
            adjustmentTypes={adjustmentTypes}
          />
          <Attachments formik={formik} />
          <Box
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: "#FAFAFA",
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
            }}
          >
            <OptionalOrderDetails formik={formik} />
          </Box>
          <AdditionalDetails formik={formik} />
        </form>
      </LocalizationProvider>
      {(loading ||
        showLoading ||
        projectsLoading ||
        isReorderInitialLoading) && <Loading />}
      <ErrorNotification
        show={showErrorNotification}
        message={errorMessage}
        onClose={() => setShowErrorNotification(false)}
      />
      <Notification
        show={showSuccessNotification}
        message={successMessage}
        onClose={() => setShowSuccessNotification(false)}
      />
    </DialogContainer>
  );
}
